import { motion } from "motion/react";
import { 
  Sparkles, 
  Leaf, 
  Droplets, 
  Filter, 
  Sun, 
  Spade,
  ArrowRight,
  Calculator,
  Wrench
} from "lucide-react";

interface ToolsShowcaseProps {
  navigate: (path: string) => void;
  tools?: any[];
}

function getToolIcon(iconName: string) {
  switch (iconName) {
    case "Droplets":
    case "Droplet":
    case "watering":
      return Droplets;
    case "Filter":
    case "filter":
    case "ph":
      return Filter;
    case "Sun":
    case "sun":
    case "sunlight":
      return Sun;
    case "Spade":
    case "spade":
    case "fertilizer":
    case "shovel":
      return Spade;
    case "Sparkles":
    case "sparkles":
      return Sparkles;
    case "Leaf":
    case "leaf":
      return Leaf;
    default:
      return Wrench;
  }
}

export default function ToolsShowcase({ navigate, tools = [] }: ToolsShowcaseProps) {
  // Use provided tools, otherwise default back to robust fallback static set
  const activeTools = tools.length > 0 ? tools : [
    {
      id: "t_flower",
      name: "Flower Identifier",
      description: "Easily catalog wildflower varieties, bloom structures, and custom growth timelines.",
      link: "/tools/flower-identifier",
      icon: "Sparkles",
      status: "Active"
    },
    {
      id: "t_tree",
      name: "Trees Identifier",
      description: "Classify broadleaf & needle evergreen structures, trunk barks, and forest specimens.",
      link: "/tools/tree-identifier",
      icon: "Leaf",
      status: "Active"
    },
    {
      id: "t_weed",
      name: "Weeds & Shrub Identifier",
      description: "Identify invasive garden weeds, lawn grass types, and wild woody shrubs accurately.",
      link: "/tools/weed-identifier",
      icon: "Leaf",
      status: "Active"
    },
    {
      id: "t_disease",
      name: "Plant Disease Identifier",
      description: "Diagnose leaf spots, mold infections, black spots, and bacterial blight syndromes.",
      link: "/tools/disease-identifier",
      icon: "Activity",
      status: "Active"
    },
    {
      id: "t_water",
      name: "Watering Calculator",
      description: "Calculate custom irrigation cycles based on temperature, location, and soil types.",
      link: "/tools/watering-calculator",
      icon: "Droplets",
      status: "Active"
    },
    {
      id: "t_ph",
      name: "Soil pH Analyzer",
      description: "Learn how to diagnose and optimize soil acidity levels for standard house crops.",
      link: "/tools/soil-ph-analyzer",
      icon: "Filter",
      status: "Active"
    },
    {
      id: "t_sun",
      name: "Direct Sunlight Meter",
      description: "Determine daily light exposure thresholds for broadleaf indoor decorative items.",
      link: "/tools/sunlight-exposure-meter",
      icon: "Sun",
      status: "Active"
    },
    {
      id: "t_fertilizer",
      name: "Fertilization Planner",
      description: "Formulate healthy ratios of Nitrogen, Phosphorus, and Potassium for healthy foliage.",
      link: "/tools/fertilization-planner",
      icon: "Spade",
      status: "Active"
    }
  ];

  const publishedTools = activeTools.filter((t) => t.status !== "Draft");

  const identifiersList = publishedTools.filter((t) => {
    const term = ((t.name || "") + " " + (t.id || "") + " " + (t.description || "")).toLowerCase();
    return (
      term.includes("identifier") ||
      term.includes("identify") ||
      term.includes("scan") ||
      term.includes("flower") ||
      term.includes("tree")
    );
  });

  const calculatorsList = publishedTools.filter((t) => !identifiersList.some((idx) => idx.id === t.id));

  return (
    <section id="tools-showcase" className="py-24 bg-gray-50/80 relative overflow-hidden border-t border-b border-gray-100">
      {/* Decorative gradients */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none -z-10">
        <div className="absolute top-12 left-10 w-96 h-96 bg-green-500/[0.015] rounded-full blur-3xl" />
        <div className="absolute bottom-12 right-10 w-96 h-96 bg-emerald-500/[0.015] rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Section Title Area */}
        <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-green-50/70 border border-green-150/45 rounded-full text-green-700 text-xs font-black uppercase tracking-widest shadow-xs select-none"
          >
            <Calculator className="w-3.5 h-3.5" /> Professional Botanical Suite
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="text-4xl sm:text-5xl font-[900] text-gray-950 tracking-tight leading-[1.12]"
          >
            Everything You Need To <br />
            Identify Plants Smarter
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="text-base sm:text-lg text-gray-500 font-medium max-w-[650px] mx-auto leading-relaxed"
          >
            Explore our specialized sub-calculators and automated diagnostic identifiers fine-tuned for smart botanical analysis.
          </motion.p>
        </div>

        {/* Categories Division */}
        <div className="space-y-16">
          
          {/* Category A: Botanical Identifiers */}
          {identifiersList.length > 0 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-3 border-b border-gray-150">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                  <Sparkles className="w-4 h-4" />
                </div>
                <h3 className="text-lg font-black text-gray-900 tracking-tight uppercase tracking-wider text-[13px]">
                  Botanical Identifiers
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {identifiersList.map((tool, idx) => {
                  const ToolIcon = getToolIcon(tool.icon);
                  return (
                    <motion.div
                      key={tool.id}
                      initial={{ opacity: 0, y: 15 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: idx * 0.1 }}
                      whileHover={{ y: -5 }}
                      onClick={() => navigate(tool.link)}
                      className="group bg-white border border-gray-150 rounded-2xl p-6 relative overflow-hidden transition-all duration-300 flex items-start gap-5 cursor-pointer shadow-3xs hover:shadow-md hover:border-emerald-200"
                    >
                      <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl group-hover:bg-emerald-100/70 flex items-center justify-center shrink-0 transition-colors duration-300">
                        <ToolIcon className="w-5 h-5" />
                      </div>
                      <div className="space-y-2 min-w-0 flex-1">
                        <h4 className="text-base font-extrabold text-gray-950 tracking-tight group-hover:text-emerald-700 transition-colors">
                          {tool.name}
                        </h4>
                        <p className="text-sm text-gray-500 font-medium leading-relaxed">
                          {tool.description}
                        </p>
                        <div className="pt-2 flex items-center gap-1.5 text-xs font-bold text-emerald-600 group-hover:text-emerald-700 transition-all">
                          <span>Launch Identifier</span>
                          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Category B: Gardening Calculators */}
          {calculatorsList.length > 0 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-3 border-b border-gray-150">
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center text-green-600">
                  <Calculator className="w-4 h-4" />
                </div>
                <h3 className="text-lg font-black text-gray-900 tracking-tight uppercase tracking-wider text-[13px]">
                  Gardening Calculators
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {calculatorsList.map((tool, idx) => {
                  const ToolIcon = getToolIcon(tool.icon);
                  return (
                    <motion.div
                      key={tool.id}
                      initial={{ opacity: 0, y: 15 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: idx * 0.08 }}
                      whileHover={{ y: -5 }}
                      onClick={() => navigate(tool.link)}
                      className="group bg-white border border-gray-150 rounded-2xl p-6.5 relative overflow-hidden transition-all duration-300 flex flex-col justify-between cursor-pointer shadow-3xs hover:shadow-md hover:border-green-200"
                    >
                      <div className="space-y-4">
                        <div className="w-10 h-10 bg-green-50 text-green-600 rounded-lg group-hover:bg-green-100/70 flex items-center justify-center transition-colors duration-300">
                          <ToolIcon className="w-5 h-5" />
                        </div>
                        <div className="space-y-1.5">
                          <h4 className="text-sm font-extrabold text-gray-950 tracking-tight group-hover:text-green-700 transition-colors">
                            {tool.name}
                          </h4>
                          <p className="text-xs text-gray-450 leading-relaxed font-semibold">
                            {tool.description}
                          </p>
                        </div>
                      </div>
                      <div className="mt-6 pt-3.5 border-t border-gray-100/50 flex items-center justify-between text-[11px] font-bold text-gray-400 group-hover:text-green-600">
                        <span>Launch Tool</span>
                        <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-green-600 group-hover:translate-x-1 transition-all" />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

      </div>
    </section>
  );
}
